import { useEffect, useMemo, useRef } from 'react'
import { Field } from '../forms/Field'
import { Textarea } from '../forms/Controls'
import styles from './CodeField.module.css'

function jsonError(value) {
  if (!String(value || '').trim()) return null
  try {
    JSON.parse(value)
    return null
  } catch (error) {
    return `Invalid JSON: ${error.message}`
  }
}

export function CodeField({
  id,
  label,
  value,
  onChange,
  language = 'json',
  validate,
  onValidationChange,
  error,
  help,
  required = false,
  optional = false,
  rows = 8,
  className = '',
  ...props
}) {
  const controlRef = useRef(null)
  const validationChangeRef = useRef(onValidationChange)
  const validationError = useMemo(() => {
    if (error) return error
    if (validate) return validate(value)
    if (language === 'json') return jsonError(value)
    return null
  }, [error, language, validate, value])

  useEffect(() => {
    validationChangeRef.current = onValidationChange
  }, [onValidationChange])

  useEffect(() => {
    controlRef.current?.setCustomValidity(validationError || '')
    validationChangeRef.current?.(validationError)
  }, [validationError])

  return (
    <Field
      id={id}
      label={label}
      labelExtra={<span className={styles.language} aria-hidden="true">{language}{optional ? ' · optional' : ''}</span>}
      error={validationError}
      help={help}
      required={required}
      optional={optional}
      className={className}
    >
      <Textarea
        {...props}
        ref={controlRef}
        id={id}
        className={styles.control}
        data-language={language}
        value={value}
        onChange={(event) => onChange?.(event.target.value, event)}
        rows={rows}
        spellCheck={false}
      />
    </Field>
  )
}

export default CodeField
